import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Collapse } from "@mui/material";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { debounce } from "lodash";
import cx from "classnames";

import { ReactComponent as Check } from "../../assets/icons/loadingGreen.svg";
import { ICONS, Icon } from "../Icons";
import { setAccordionStatus } from "../../store/actions/appConfig";
import { getAccordionStatuses } from "../../store/selectors/appConfig";
import { ContexualHelp } from "../Notifications";
import { StyledAccordionWrapper } from "./styled";

export const Accordion = ({
  id,
  title,
  children,
  showDefinition,
  isCompleted = false,
  subTitle,
  classes: extendedClasses,
  showHelperText,
  accordionRef,
  expandedDescription,
  ...others
}) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState("");
  const transitionProps = {
    mountOnEnter: false,
    unmountOnExit: false
  };
  const accordionStatuses = useSelector(getAccordionStatuses);
  const statuses = JSON.parse(accordionStatuses);

  const [initialStatus, setInitialStatus] = useState(false);
  useEffect(() => {
    setInitialStatus(statuses[id]);
  }, []);

  const handleChange = panel => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
    if (newExpanded && isCompleted) {
      const updatedStatuses = {};
      updatedStatuses[id] = isCompleted;
      dispatch(setAccordionStatus(updatedStatuses));
    }
  };

  const updateAccordionStatus = useCallback(
    debounce(updatedStatuses => {
      dispatch(setAccordionStatus(updatedStatuses));
    }, 100),
    []
  );

  useLayoutEffect(() => {
    const updatedStatuses = {};
    if (id === "isTaxDeclarationCompleted") {
      if ((initialStatus && isCompleted) || (expanded && isCompleted)) {
        updatedStatuses[id] = isCompleted;
      } else {
        updatedStatuses[id] = false;
      }
    } else {
      updatedStatuses[id] = isCompleted;
    }
    updateAccordionStatus(updatedStatuses);
  }, [initialStatus, id, isCompleted]);

  const byDefaultExpandedAccordion = [
    "documentLanding",
    "companyInformation",
    "stakeholderInformation",
    "productInformation",
    "codesBankUseReview",
    "checks",
    "companyInfoAccordion",
    "shareholdersDetails"
  ];

  const expandedDescriptionView = [
    "isSelectPackage",
    "preferences",
    "authorizations",
    "communication",
    "codesBankUse"
  ];

  return (
    <StyledAccordionWrapper className={"accordion"} {...others}>
      <MuiAccordion
        defaultExpanded={byDefaultExpandedAccordion.includes(id)}
        onChange={handleChange(id)}
        square
        TransitionComponent={Collapse}
        TransitionProps={transitionProps}
        data-testid="accordion"
        open={expanded}
      >
        <MuiAccordionSummary
          expandIcon={<Icon name={ICONS.arrowDown} alt="arrow-down" className={"expandIcon"} />}
          ref={accordionRef}
        >
          <div className={cx("accordionSummaryWrapper", "customAccordionSummaryWrapper")}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span className="activePanel"></span>
              {isCompleted && statuses[id] && (
                <span data-testid="accordion-completed-status">
                  <Check size="16px" className={"success"} />
                </span>
              )}
              <div className="accordionTitle">
                <div className="title" data-testid="accordion-title">
                  {title}
                  {showHelperText && (
                    <ContexualHelp
                      title={showHelperText}
                      placement="right"
                      isDisableHoverListener={false}
                      dataTestId="accordion-help"
                    >
                      <HelpOutlineIcon className="helperIcon" />
                    </ContexualHelp>
                  )}
                </div>
                {subTitle && <p className="subTitle">{subTitle}</p>}
                {expandedDescriptionView.includes(expanded) && expandedDescription && (
                  <p className="expandedDescription">{expandedDescription}</p>
                )}
              </div>
            </div>
            <div>
              {(expanded === "isTaxDeclarationCompleted" ||
                expanded === "stakeholderTaxDeclarationSection") &&
                showDefinition}
            </div>
          </div>
        </MuiAccordionSummary>
        <MuiAccordionDetails
          classes={{ root: cx("accordionDetails", extendedClasses?.accordionDetails) }}
          data-testid="accordion-details"
        >
          {children}
        </MuiAccordionDetails>
      </MuiAccordion>
    </StyledAccordionWrapper>
  );
};
